import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Maximize2, Sparkles, Send, MessageCircle, LogIn } from 'lucide-react';
import { postApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import LoginModal from '../components/LoginModal';
import './DanmakuPage.css';

const DanmakuPage = ({ onBack }) => {
  const { user } = useAuth();
  const [hotPosts, setHotPosts] = useState([]);
  const [scrollingPosts, setScrollingPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]); // 最近2分钟的新帖子
  const [quickPostContent, setQuickPostContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [showQuickPost, setShowQuickPost] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // 加载热门吐槽 Top10
  const loadHotPosts = useCallback(async () => {
    try {
      const posts = await postApi.getRanking(10);
      setHotPosts(posts);
    } catch (error) {
      console.error('加载热门吐槽失败:', error);
    }
  }, []);

  // 加载最新吐槽并管理最近发布的内容
  const loadLatestPosts = useCallback(async () => {
    try {
      const result = await postApi.getList(1, 50);
      const newPosts = result.records || [];
      
      // 检查最近2分钟内的新帖子
      const now = Date.now();
      const recentNewPosts = newPosts.filter(post => {
        const postTime = new Date(post.createTime).getTime();
        return now - postTime < 2 * 60 * 1000; // 2分钟
      });

      // 更新最近帖子列表，添加新的并移除过期的
      setRecentPosts(prevRecent => {
        const updated = [...prevRecent];
        
        // 添加新帖子
        recentNewPosts.forEach(newPost => {
          if (!updated.find(p => p.id === newPost.id)) {
            updated.push({
              ...newPost,
              addedAt: now,
              isNew: true
            });
          }
        });
        
        // 移除超过2分钟的帖子
        return updated.filter(post => now - post.addedAt < 2 * 60 * 1000);
      });

      setScrollingPosts(newPosts);
    } catch (error) {
      console.error('加载最新吐槽失败:', error);
    }
  }, []);

  // 快速发布弹幕
  const handleQuickPost = async () => {
    if (!quickPostContent.trim() || isPosting) return;
    
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    setIsPosting(true);
    try {
      await postApi.create({
        content: quickPostContent.trim(),
        tags: [] // 快速发布不添加标签
      });
      
      setQuickPostContent('');
      setShowQuickPost(false);
      
      // 立即刷新数据以显示新发布的内容
      loadLatestPosts();
    } catch (error) {
      console.error('发布弹幕失败:', error);
      alert('发布失败，请重试');
    } finally {
      setIsPosting(false);
    }
  };

  // 处理登录成功
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // 登录成功后可以继续发布
  };

  // 处理键盘事件
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuickPost();
    }
  };

  useEffect(() => {
    loadHotPosts();
    loadLatestPosts();

    // 定时刷新
    const hotInterval = setInterval(loadHotPosts, 5 * 60 * 1000); // 5分钟
    const latestInterval = setInterval(loadLatestPosts, 15 * 1000); // 15秒检查新内容

    return () => {
      clearInterval(hotInterval);
      clearInterval(latestInterval);
    };
  }, [loadHotPosts, loadLatestPosts]);

  // 容器动画变体
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <div className="danmaku-page">
      {/* 顶部控制栏 */}
      <motion.div 
        className="danmaku-controls"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.button 
          className="control-btn back-btn"
          onClick={onBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft className="w-5 h-5" />
          返回主页
        </motion.button>
        <div className="page-title">
          <Sparkles className="w-6 h-6" />
          弹幕大屏 - 实时吐槽展示
        </div>
        <div className="danmaku-user-section">
          {user ? (
            <div className="user-info">
              <span className="user-welcome">欢迎, {user.username}</span>
            </div>
          ) : (
            <motion.button
              className="login-btn"
              onClick={() => setShowLoginModal(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogIn className="w-4 h-4" />
              登录
            </motion.button>
          )}
          <div className="live-indicator">
            <div className="live-dot"></div>
            LIVE
          </div>
        </div>
      </motion.div>

      {/* 上部分：Top10 热点吐槽 */}
      <motion.div 
        className="danmaku-section hot-section"
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="section-label">🔥 热点吐槽 TOP10</div>
        <motion.div 
          className="hot-posts-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {hotPosts.map((post, index) => (
            <motion.div
              key={post.id}
              className="hot-post-item"
              variants={itemVariants}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
            >
              <motion.span 
                className="rank-badge"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.1 + 0.5, type: "spring" }}
              >
                #{index + 1}
              </motion.span>
              <span className="post-content">{post.content}</span>
              <motion.span 
                className="post-stats"
                whileHover={{ scale: 1.1 }}
              >
                🤮 {post.reactions?.disgusted || 0}
              </motion.span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* 下部分：实时弹幕流 */}
      <motion.div 
        className="danmaku-section scrolling-section"
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <div className="section-label">💬 实时弹幕流</div>
        <div className="scrolling-container">
          {/* 最新帖子特殊显示行 */}
          <AnimatePresence>
            {recentPosts.length > 0 && (
              <motion.div
                className="recent-posts-row"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {recentPosts.map((post) => (
                  <motion.div
                    key={post.id}
                    className="recent-post-item"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    whileHover={{ 
                      scale: 1.05,
                      y: -3,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <span className="new-badge">🆕</span>
                    <span className="scrolling-username">@{post.username}:</span>
                    <span className="scrolling-content">{post.content}</span>
                    <motion.span 
                      className="scrolling-reactions"
                      whileHover={{ scale: 1.2 }}
                    >
                      🤮{post.reactions?.disgusted || 0}
                    </motion.span>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* 常规滚动弹幕 */}
          {[0, 1, 2].map((row) => (
            <motion.div
              key={row}
              className="scrolling-row"
              initial={{ x: row % 2 === 0 ? '100%' : '-100%' }}
              animate={{ x: row % 2 === 0 ? '-100%' : '100%' }}
              transition={{
                duration: 25 + row * 3,
                repeat: Infinity,
                ease: 'linear',
              }}
            >
              {scrollingPosts
                .filter((_, i) => i % 3 === row)
                .filter(post => !recentPosts.find(rp => rp.id === post.id)) // 排除已在最新显示的
                .map((post) => (
                  <motion.div 
                    key={post.id} 
                    className="scrolling-post"
                    whileHover={{ 
                      scale: 1.05,
                      y: -3,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <span className="scrolling-username">@{post.username}:</span>
                    <span className="scrolling-content">{post.content}</span>
                    <motion.span 
                      className="scrolling-reactions"
                      whileHover={{ scale: 1.2 }}
                    >
                      🤮{post.reactions?.disgusted || 0}
                    </motion.span>
                  </motion.div>
                ))}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* 快速发布弹幕浮动按钮和输入框 */}
      <AnimatePresence>
        {!showQuickPost && (
          <motion.button
            className="quick-post-fab"
            onClick={() => setShowQuickPost(true)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showQuickPost && (
          <motion.div
            className="quick-post-panel"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="quick-post-header">
              <span className="quick-post-title">💬 快速发布弹幕</span>
              <button 
                className="quick-post-close"
                onClick={() => setShowQuickPost(false)}
              >
                ✕
              </button>
            </div>
            <div className="quick-post-input-container">
              <textarea
                className="quick-post-input"
                placeholder={user ? "说点什么吧..." : "请先登录后发布弹幕"}
                value={quickPostContent}
                onChange={(e) => setQuickPostContent(e.target.value)}
                onKeyPress={handleKeyPress}
                maxLength={500}
                disabled={!user || isPosting}
                rows={3}
              />
              <div className="quick-post-actions">
                <span className="char-count">
                  {quickPostContent.length}/500
                </span>
                <motion.button
                  className="quick-post-send"
                  onClick={handleQuickPost}
                  disabled={!user || !quickPostContent.trim() || isPosting}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isPosting ? (
                    <div className="loading-spinner" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      发送
                    </>
                  )}
                </motion.button>
              </div>
            </div>
            {!user && (
              <div className="quick-post-login-hint">
                <span>请先登录后再发布弹幕</span>
                <motion.button
                  className="quick-login-btn"
                  onClick={() => setShowLoginModal(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <LogIn className="w-4 h-4" />
                  立即登录
                </motion.button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 登录模态框 */}
      {showLoginModal && (
        <LoginModal onClose={handleLoginSuccess} />
      )}
    </div>
  );
};

export default DanmakuPage;